import React, { useEffect, useState } from 'react';
import LazyLoad from 'react-lazyload';
import { useNavigate } from 'react-router-dom';

import FilledBadge from './Badge/FilledBadge';
import { SALES_TYPE_ASSURANCE } from '@/const/products';
import { asYYYYMM, formatDistanceToThousandKm, formatPrice } from '@/utils/common';
import { getSalesTypeDisplayDesc } from '@/utils/Products/productUtils';
import '@egjs/react-flicking/dist/flicking.css';
import Skeleton from '@mui/material/Skeleton';

interface ProductCardProps {
  products: ProductsList;
  index: number;
}

type LiceseProps = {
  truckNumber: string | undefined;
};

const LicensePlate = ({ truckNumber }: LiceseProps) => {
  return (
    <div className="border-2 rounded-[4px] border-gray-8 bg-[#FFC900] w-fit">
      <div className="m-[1.5px] border rounded-[4px]  border-gray-8 text-[10px] font-bold text-gray-8 px-[4px] py-[3px]">
        {truckNumber}
      </div>
    </div>
  );
};

export default function ProductCard({ products, index }: ProductCardProps) {
  const navigate = useNavigate();
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isMaintenanceSale, setIsMaintenanceSale] = useState(false);

  const fetchDetail = (id: number) => {
    navigate('/products/' + id, { state: { isPublicDetail: true } });
  };

  const isShowSalesType = (salesType: string) => {
    return salesType !== SALES_TYPE_ASSURANCE;
  };

  const priceDownAmount = products?.oldPrice && products?.price ? products.oldPrice - products.price : 0;
  const isPriceDown = priceDownAmount > 0;

  useEffect(() => {
    const isMaintenance =
      products?.salesType?.code === 'ASSURANCE' && products?.productStatus?.code === 'MAINTENANCE_SALE';
    setIsMaintenanceSale(isMaintenance);
  }, [products?.salesType?.code, products?.productStatus?.code]);

  return (
    <div
      onClick={() => {
        fetchDetail(products?.id);
      }}
    >
      <ul className="mt-4">
        {
          <li className={`p-1`}>
            <div className="flex w-full border border-gray-100 rounded-lg h-[15rem] relative">
              <LazyLoad className="w-full h-full" offset={100}>
                {isImageLoading && (
                  <div className={`flex w-full border border-gray-100 rounded-lg h-[15rem]`}>
                    <Skeleton variant="rounded" width="100%" height="100%" animation="wave" />
                  </div>
                )}
                <img
                  className={`w-full h-full rounded-lg object-cover  ${isImageLoading ? 'invisible' : 'block'}`}
                  src={products?.representImageUrl}
                  alt="트럭 사진"
                  style={{ maxWidth: '100%', maxHeight: '100', objectFit: 'cover' }}
                  onLoad={(e) => {
                    setIsImageLoading(false);
                  }}
                  width={100}
                  height={100}
                />
                {/* {products?.salesType && isShowSalesType(products.salesType.code) && ( */}
                {products?.salesType && (
                  <div className="absolute top-0 left-0 p-3">
                    <FilledBadge
                      textColor="text-gray-0"
                      bgColor={
                        products?.salesType?.code === 'CONSIGNMENT'
                          ? 'blueGradient.blue-to-blue'
                          : products?.salesType?.code === 'NORMAL'
                          ? 'primary-10'
                          : products?.salesType?.code === 'ASSURANCE'
                          ? 'green'
                          : products?.salesType?.code === 'THIRD_PARTY_DEALER'
                          ? 'gray'
                          : 'primary-10'
                      }
                      rounded="rounded"
                      fontWeight="font-bold"
                      text={getSalesTypeDisplayDesc(products?.salesType)}
                    />
                  </div>
                )}
                <div className="absolute bottom-0 left-0 w-full h-[50px] p-3 rounded-b-lg bg-gradient-to-t from-[rgba(0,0,0,0.7)] to-[rgba(0,0,0,0)]">
                  <div className="absolute bottom-0 left-0 p-3 ">
                    <span className="text-gray-0">매물번호 {products?.productsNumber}</span>
                  </div>
                </div>
              </LazyLoad>
            </div>

            {products?.salesType?.code === 'ASSURANCE' &&
              (isMaintenanceSale ? (
                <div className="bg-[#45A186] text-gray-0 rounded-lg py-[10px] px-2 text-[18px] font-bold mt-3 text-center">
                  {/* <div className="bg-grey-7 text-gray-0 rounded-lg py-3 px-2 text-[18px] font-bold mt-3 text-center"> */}
                  입고 점검중
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 bg-[#EBF6EE] border border-[#00741F] text-grey-9 rounded-lg py-3 px-2 text-[18px] font-bold mt-3 text-center">
                  <img
                    src={`https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/InspectionCompleted.png`}
                    alt="inspection-completed"
                    width={24}
                    height={24}
                  />
                  입고 점검완료
                </div>
              ))}

            <dt className="mt-2 mb-1 flow-root text-xl font-semibold text-gray-8">
              {products?.youtubeUrl && (
                <img
                  className="float-left mt-0.5 mr-2 h-6 w-6"
                  src="https://zigtruck-service-public-image.s3.ap-northeast-2.amazonaws.com/youtube_icon.png"
                  alt="youtube"
                />
              )}
              <span className="break-keep">{products?.truckName}</span>
            </dt>
            <dd className="flex flex-wrap items-center text-gray-6 mt-1 mb-2 text-[16px] leading-[20px]">
              <span>{asYYYYMM(products?.firstRegistrationDate)}</span>
              <span className="ml-1">• {products?.transmission.desc}</span>
              <span className="ml-1">• {formatDistanceToThousandKm(products?.distance)}만km</span>
              <span className="ml-1">• {products?.loadedInnerLength}m</span>
              <span className="ml-1">• {products?.power}마력</span>
              <span className="ml-1">{!!products?.palletCount && `• 파렛트 ${products?.palletCount}개`}</span>
              {(products?.area1 || products?.area2 || products?.area3) && (
                <span>
                  • {products?.area1}{' '}
                  {products?.area2 || products?.area3
                    ? `(${products?.area2}${products?.area2 && products?.area3 ? ',' : ''}${products?.area3})`
                    : ''}
                </span>
              )}
            </dd>
            {products?.price && (
              <>
                {isPriceDown && (
                  <dd className="flex flex-wrap items-center gap-2">
                    <span className="text-[16px] text-[#9CA3AF] line-through">
                      {formatPrice(products?.oldPrice?.toString())}
                    </span>
                    <span className="bg-[#EF4444] text-gray-0 rounded-md px-2 py-[3px] text-[14px] font-bold">
                      -{formatPrice(priceDownAmount.toString())} 인하
                    </span>
                  </dd>
                )}
                <dd className="flex items-center my-2">
                  {/* <span className="flex-1"> */}
                  <span className="font-semibold text-[22px]">{formatPrice(products?.price?.toString())}</span>
                  {products?.isLicense === 1 && (
                    <div className="flex items-center ml-2">
                      <LicensePlate truckNumber={products?.truckNumber} />
                    </div>
                  )}
                  {/* </span> */}
                </dd>
              </>
            )}
          </li>
        }
      </ul>
    </div>
  );
}
